/**
 * Ambience player: very quiet noise beds (looping pink noise → filter →
 * LFO-swelled gain) plus sparse synthesized events (chirps, crickets,
 * bubbles, plips) scheduled by the same lookahead timer as the music.
 * Theme changes crossfade layers, like the music decks.
 */
import { Rng } from '@dropstack/core';
import { ParamTrack, writeAutomation } from './automation.ts';
import { AMBIENCE_BEDS, planAmbienceEvent, type AmbienceEvent, type AmbienceKind } from './ambiencePlan.ts';
import type { MixGraph } from './graph.ts';
import { hashString } from './motif.ts';
import { MAX_LATENESS_S } from './scheduler.ts';
import { playVoice } from './synth.ts';
import { CHIRP_VOICE } from './timbres.ts';

const START_FADE_S = 2;
const RETIRE_GRACE_S = 0.25;

interface Layer {
  kind: AmbienceKind;
  out: GainNode;
  fader: ParamTrack;
  /** Looping noise sources (started at a random offset) and LFOs. */
  beds: AudioBufferSourceNode[];
  lfos: OscillatorNode[];
  nodes: AudioNode[];
  rng: Rng;
  pending: { gapS: number; event: AmbienceEvent } | null;
  nextEventAt: number;
  retireAt: number | null;
}

function safeDisconnect(n: AudioNode): void {
  try {
    n.disconnect();
  } catch {
    // Already disconnected.
  }
}

export class AmbiencePlayer {
  private layers: Layer[] = [];
  private created = 0;

  constructor(private readonly g: MixGraph) {}

  get active(): boolean {
    return this.layers.length > 0;
  }

  private get live(): Layer | undefined {
    return this.layers.filter((l) => l.retireAt === null).at(-1);
  }

  /** Crossfade to `kind` ('none' = fade out) starting at audio time `at`. */
  switchTo(kind: AmbienceKind | 'none', at: number, fadeS: number): void {
    const now = this.g.ctx.currentTime;
    const current = this.live;
    if (current?.kind === kind) return;
    if (!current && kind !== 'none') {
      this.createLayer(kind, at, START_FADE_S);
      return;
    }
    if (current) this.retire(current, Math.max(now, at), fadeS);
    if (kind !== 'none') this.createLayer(kind, Math.max(now, at), fadeS);
  }

  /** Fade everything out. */
  stop(fadeS: number): void {
    const now = this.g.ctx.currentTime;
    for (const l of this.layers) if (l.retireAt === null || l.retireAt > now + fadeS) this.retire(l, now, fadeS);
  }

  /** Scheduler callback: plan and schedule events that start before `until`. */
  tick(now: number, until: number): void {
    for (const layer of this.layers) {
      const limit = layer.retireAt === null ? until : Math.min(until, layer.retireAt);
      // After a stall, skip missed events instead of playing them all at once.
      while (layer.pending && layer.nextEventAt < now - MAX_LATENESS_S) this.advance(layer);
      while (layer.pending && layer.nextEventAt < limit) {
        this.renderEvent(layer, layer.pending.event, layer.nextEventAt);
        this.advance(layer);
      }
    }
    this.layers = this.layers.filter((l) => {
      if (l.retireAt !== null && now > l.retireAt + RETIRE_GRACE_S) {
        this.discard(l);
        return false;
      }
      return true;
    });
  }

  dispose(): void {
    for (const l of this.layers) this.discard(l);
    this.layers = [];
  }

  private advance(layer: Layer): void {
    layer.pending = planAmbienceEvent(layer.kind, layer.rng);
    if (layer.pending) layer.nextEventAt += layer.pending.gapS;
  }

  private createLayer(kind: AmbienceKind, at: number, fadeS: number): void {
    const { ctx, ambienceIn, noise } = this.g;
    const out = ctx.createGain();
    out.gain.value = 0;
    out.connect(ambienceIn);
    const rng = new Rng(hashString(kind) + this.created++);
    const layer: Layer = { kind, out, fader: new ParamTrack(0), beds: [], lfos: [], nodes: [out], rng, pending: null, nextEventAt: at, retireAt: null };

    for (const bed of AMBIENCE_BEDS[kind]) {
      const src = ctx.createBufferSource();
      src.buffer = noise.pink;
      src.loop = true;
      const filter = ctx.createBiquadFilter();
      filter.type = bed.filter.type;
      filter.frequency.value = bed.filter.hz;
      filter.Q.value = bed.filter.q;
      const depth = bed.swell ? bed.swell.depth / 2 : 0;
      const level = ctx.createGain();
      level.gain.value = bed.gain * (1 - depth);
      src.connect(filter);
      filter.connect(level);
      level.connect(out);
      layer.beds.push(src);
      layer.nodes.push(src, filter, level);

      if (bed.swell) {
        const lfo = ctx.createOscillator();
        lfo.frequency.value = bed.swell.hz;
        const lfoDepth = ctx.createGain();
        lfoDepth.gain.value = bed.gain * depth;
        lfo.connect(lfoDepth);
        lfoDepth.connect(level.gain);
        layer.lfos.push(lfo);
        layer.nodes.push(lfo, lfoDepth);
      }
      if (bed.sweep) {
        const lfo = ctx.createOscillator();
        lfo.frequency.value = bed.sweep.hz;
        const lfoDepth = ctx.createGain();
        lfoDepth.gain.value = bed.sweep.depthHz;
        lfo.connect(lfoDepth);
        lfoDepth.connect(filter.frequency);
        layer.lfos.push(lfo);
        layer.nodes.push(lfo, lfoDepth);
      }
    }
    const offset = rng.next() * noise.pink.duration;
    for (const s of layer.beds) s.start(at, offset);
    for (const s of layer.lfos) s.start(at);

    this.advance(layer);
    this.layers.push(layer);
    writeAutomation(out.gain, ctx.currentTime, layer.fader.rampTo(1, ctx.currentTime, at, at + fadeS));
  }

  private retire(layer: Layer, at: number, fadeS: number): void {
    const now = this.g.ctx.currentTime;
    writeAutomation(layer.out.gain, now, layer.fader.rampTo(0, now, at, at + fadeS));
    layer.retireAt = at + fadeS;
    for (const s of [...layer.beds, ...layer.lfos]) {
      try {
        s.stop(at + fadeS + RETIRE_GRACE_S);
      } catch {
        // Some older engines reject a second stop(); the first one still applies.
      }
    }
  }

  private discard(layer: Layer): void {
    for (const n of layer.nodes) safeDisconnect(n);
  }

  private renderEvent(layer: Layer, event: AmbienceEvent, at: number): void {
    const { ctx } = this.g;
    let out: AudioNode = layer.out;
    let panner: StereoPannerNode | null = null;
    if (typeof ctx.createStereoPanner === 'function') {
      panner = ctx.createStereoPanner();
      panner.pan.value = event.pan;
      panner.connect(layer.out);
      out = panner;
    }
    let remaining = event.blips.length;
    const done = (): void => {
      remaining--;
      if (remaining <= 0 && panner) safeDisconnect(panner);
    };
    if (remaining === 0) done();
    for (const b of event.blips) {
      playVoice(ctx, out, CHIRP_VOICE, { hz: b.hz, glideToHz: b.toHz, glideS: b.dur, time: at + b.at, dur: b.dur, velocity: b.gain }, undefined, done);
    }
  }
}
